import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Shield, Users, Settings, Truck, Wrench, ClipboardCheck, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPS = [
  { app_id: 'ridero', name: 'Ridero', icon: Truck, color: '#3B82F6' },
  { app_id: 'mechanic_kiosk', name: 'MechanicKiosk', icon: Wrench, color: '#10B981' },
  { app_id: 'pti_pector', name: 'PTI Pector', icon: ClipboardCheck, color: '#F97316' },
];

export default function AdminControlPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedApp, setSelectedApp] = useState('ridero');
  const [appAdmins, setAppAdmins] = useState([]);
  const [permissionDefs, setPermissionDefs] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  
  // Permission edit dialog
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editPermissions, setEditPermissions] = useState({});

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApp]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [usersRes, permsDefsRes, appAdminsRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/admin-permissions`, { withCredentials: true }),
        axios.get(`${API}/admin-permissions/${selectedApp}`, { withCredentials: true })
      ]);
      
      setUsers(usersRes.data);
      setPermissionDefs(permsDefsRes.data);
      setAppAdmins(appAdminsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetAppRole = async (userId, roleId) => {
    try {
      await axios.patch(
        `${API}/users/${userId}/app-role?app_id=${selectedApp}&role_id=${roleId}`,
        {},
        { withCredentials: true }
      );
      toast.success('App role updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const openPermissionDialog = async (adminUserId) => {
    try {
      const res = await axios.get(
        `${API}/admin-permissions/${selectedApp}/${adminUserId}`,
        { withCredentials: true }
      );
      setEditingAdmin(adminUserId);
      setEditPermissions(res.data.permissions || {});
    } catch (error) {
      toast.error('Failed to load permissions');
    }
  };

  const savePermissions = async () => {
    try {
      await axios.put(
        `${API}/admin-permissions/${selectedApp}/${editingAdmin}`,
        { permissions: editPermissions },
        { withCredentials: true }
      );
      toast.success('Permissions saved');
      setEditingAdmin(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save permissions');
    }
  };

  const togglePermission = (permId) => {
    setEditPermissions(prev => ({
      ...prev,
      [permId]: !prev[permId]
    }));
  };

  const selectedAppInfo = APPS.find(a => a.app_id === selectedApp);
  const AppIcon = selectedAppInfo?.icon || Shield;

  // Get admins for the selected app
  const appAdminUsers = users.filter(u => u.app_roles?.[selectedApp] === 'admin');

  if (user?.role_id !== 'master_admin') {
    return (
      <DashboardLayout>
        <Header title="Access Denied" subtitle="Master Admin access required" />
        <div className="p-6">
          <Card>
            <CardContent className="p-12 text-center">
              <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Master Admin Only</h2>
              <p className="text-muted-foreground">
                This page is only accessible to Master Admins.
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Header title="Admin Control" subtitle="Manage app-specific admin permissions" />
      
      <div className="p-6 space-y-6">
        {/* App Selector */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">Select App:</span>
              <div className="flex gap-2">
                {APPS.map((app) => {
                  const Icon = app.icon;
                  return (
                    <Button
                      key={app.app_id}
                      variant={selectedApp === app.app_id ? 'default' : 'outline'}
                      onClick={() => setSelectedApp(app.app_id)}
                      className="flex items-center gap-2"
                      style={selectedApp === app.app_id ? { backgroundColor: app.color } : {}}
                      data-testid={`select-app-${app.app_id}`}
                    >
                      <Icon className="w-4 h-4" />
                      {app.name}
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* App Admins */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${selectedAppInfo?.color}20` }}
              >
                <AppIcon className="w-5 h-5" style={{ color: selectedAppInfo?.color }} />
              </div>
              <div>
                <CardTitle className="font-secondary uppercase">{selectedAppInfo?.name} Admins</CardTitle>
                <CardDescription>Manage admin permissions for this app</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">Loading...</div>
            ) : appAdminUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No admins assigned to this app yet. Assign admin role to users below.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Admin</TableHead>
                    <TableHead>Permissions</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {appAdminUsers.map((adminUser) => {
                    const adminPerms = appAdmins.find(a => a.admin_user_id === adminUser.user_id);
                    const enabledCount = adminPerms 
                      ? Object.values(adminPerms.permissions || {}).filter(Boolean).length 
                      : Object.values(permissionDefs).filter(p => p.default).length;
                    
                    return (
                      <TableRow key={adminUser.user_id} className="table-row-hover">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-medium">
                              {adminUser.name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium">{adminUser.name}</p>
                              <p className="text-xs text-muted-foreground">{adminUser.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {enabledCount}/{Object.keys(permissionDefs).length} enabled
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openPermissionDialog(adminUser.user_id)}
                            data-testid={`edit-perms-${adminUser.user_id}`}
                          >
                            <Settings className="w-4 h-4 mr-2" />
                            Configure
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Assign Admin Role */}
        <Card>
          <CardHeader>
            <CardTitle className="font-secondary uppercase">Assign App Roles</CardTitle>
            <CardDescription>Set user roles for {selectedAppInfo?.name}</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Global Role</TableHead>
                  <TableHead>App Role</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.filter(u => u.role_id !== 'master_admin').map((u) => (
                  <TableRow key={u.user_id} className="table-row-hover">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-medium">
                          {u.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium">{u.name}</p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`role-badge role-${u.role_id}`}>
                        {u.role_id?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {u.app_roles?.[selectedApp] || 'user'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Select 
                        value={u.app_roles?.[selectedApp] || 'user'}
                        onValueChange={(val) => handleSetAppRole(u.user_id, val)}
                      >
                        <SelectTrigger className="w-32" data-testid={`role-select-${u.user_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="moderator">Moderator</SelectItem>
                          <SelectItem value="user">User</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Permission Edit Dialog */}
      <Dialog open={!!editingAdmin} onOpenChange={() => setEditingAdmin(null)}>
        <DialogContent className="max-w-lg" data-testid="permission-dialog">
          <DialogHeader>
            <DialogTitle>Configure Admin Permissions</DialogTitle>
            <DialogDescription>
              Toggle permissions for this admin on {selectedAppInfo?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {Object.entries(permissionDefs).map(([permId, permDef]) => (
              <div key={permId} className="flex items-center justify-between py-2 border-b border-border/50">
                <div>
                  <Label className="font-medium">{permDef.label}</Label>
                  <p className="text-xs text-muted-foreground">{permId}</p>
                </div>
                <Switch
                  checked={editPermissions[permId] ?? permDef.default}
                  onCheckedChange={() => togglePermission(permId)}
                  data-testid={`perm-switch-${permId}`}
                />
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingAdmin(null)}>Cancel</Button>
            <Button onClick={savePermissions} data-testid="save-perms-btn">
              <Save className="w-4 h-4 mr-2" />
              Save Permissions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
